import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, Button, DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import { Timestamp } from 'firebase/firestore';
import { CommunicationTagKey, PhoneCallDirection, PhoneCallRecord, TelephoneNumber } from '#types';
import { MAX_LENGTHS } from '#types/System.Parameters.types';
import FlexCol from '../../../../atoms/FlexCol';
import FlexBox from '../../../../atoms/FlexBox';
import TelephoneUI from '../../TelephoneUI/TelephoneUI';
import { useContactSearch, type ContactOption } from '../../../../../use/useContactSearch';

export type PhoneCallFormData = {
    tag: CommunicationTagKey;
    timestamp: Timestamp;
    direction?: PhoneCallDirection;
    phoneNumber?: TelephoneNumber;
    notes?: string;
    contactEntityRecordId?: string;
};

interface PhoneCallPopoverFormProps {
    initialValues?: PhoneCallRecord;
    onSave: (data: PhoneCallFormData) => boolean | Promise<boolean>;
    onCancel: () => void;
    disabled?: boolean;
}

type PhoneCallFormValues = {
    tag: CommunicationTagKey;
    timestamp: dayjs.Dayjs;
    direction?: PhoneCallDirection;
    phoneNumber?: TelephoneNumber;
    notes?: string;
    contactEntityRecordId?: string;
};

const PhoneCallPopoverForm: FC<PhoneCallPopoverFormProps> = ({
    initialValues,
    onSave,
    onCancel,
    disabled = false,
}) => {
    const [form] = Form.useForm<PhoneCallFormValues>();
    const [searchValue, setSearchValue] = useState('');
    const lastAutoFilledContactRef = useRef<string | undefined>(undefined);

    const initialContactId = initialValues?.contactEntityRecordId;
    const { options, includedOptions, loading, search, contactEntityLabel } = useContactSearch({
        kind: 'phone',
        includeDocIds: initialContactId ? [initialContactId] : undefined,
    });

    /** The contact already on the entry stays listed, so its name renders instead of its id */
    const selectOptions = useMemo(() => {
        const listed = new Set(includedOptions.map((option) => option.value));
        return [...includedOptions, ...options.filter((option) => !listed.has(option.value))];
    }, [includedOptions, options]);

    const handleSearch = (nextQuery: string) => {
        setSearchValue(nextQuery);
        search(nextQuery);
    };

    const handleContactChange = (value: string | undefined, option?: ContactOption | ContactOption[]) => {
        setSearchValue('');

        if (!value) {
            lastAutoFilledContactRef.current = undefined;
            form.setFieldsValue({ contactEntityRecordId: undefined });
            return;
        }

        const selectedOption = Array.isArray(option) ? option[0] : option;
        if (!selectedOption) {
            return;
        }

        const shouldAutoFill = lastAutoFilledContactRef.current !== value;

        if (shouldAutoFill) {
            const updates: Partial<PhoneCallFormValues> = {
                contactEntityRecordId: value,
            };

            if (selectedOption.contactValue) {
                updates.phoneNumber = { formatted: selectedOption.contactValue };
            }

            const currentNotes = form.getFieldValue('notes');
            if (!currentNotes && selectedOption.label) {
                updates.notes = `Contact: ${selectedOption.label}`;
            }

            form.setFieldsValue(updates);
            lastAutoFilledContactRef.current = value;
        } else {
            form.setFieldsValue({ contactEntityRecordId: value });
        }
    };

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                tag: initialValues.tag || 'manual',
                timestamp: initialValues.timestamp ? dayjs(initialValues.timestamp.toDate()) : dayjs(),
                direction: initialValues.direction,
                phoneNumber: initialValues.phoneNumber,
                notes: initialValues.notes,
                contactEntityRecordId: initialValues.contactEntityRecordId,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                tag: 'manual',
                timestamp: dayjs(),
            });
        }
    }, [form, initialValues]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const success = await onSave({
                tag: values.tag,
                timestamp: Timestamp.fromDate(values.timestamp.toDate()),
                direction: values.direction,
                phoneNumber: values.phoneNumber,
                notes: values.notes,
                contactEntityRecordId: values.contactEntityRecordId,
            });
            if (success) {
                form.resetFields();
            }
        } catch (error) {
            console.error('[PhoneCallPopoverForm] validation failed:', error);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <FlexCol gap={5} style={{ width: 400, maxWidth: '90vw' }}>
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    tag: 'manual',
                    timestamp: dayjs(),
                }}
                disabled={disabled}
                style={{ width: '100%' }}
                variant='filled'
            >
                {/* Tag selection removed as per requirement: defaulting to 'manual' */}
                <Form.Item
                    name="tag"
                    hidden
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="contactEntityRecordId"
                    label={contactEntityLabel}
                >
                    <Select
                        placeholder="Select contact"
                        allowClear
                        showSearch={{
                            searchValue,
                            onSearch: handleSearch,
                            filterOption: false,
                        }}
                        options={selectOptions}
                        optionRender={(option) => option.data.detail}
                        notFoundContent={
                            loading
                                ? 'Searching contacts...'
                                : searchValue.trim()
                                  ? 'No matching contacts'
                                  : 'Type to search contacts'
                        }
                        aria-label="phone-contact-select"
                        disabled={disabled}
                        onChange={handleContactChange}
                        style={{ width: '100%' }}
                        variant='filled'
                    />
                </Form.Item>

                <Form.Item
                    label="Timestamp"
                    name="timestamp"
                    rules={[{ required: true, message: 'Select the call timestamp' }]}
                >
                    <DatePicker 
                        showTime 
                        style={{ width: '100%' }} 
                        aria-label="phone-timestamp-picker"
                        variant='filled'
                    />
                </Form.Item>

                <Form.Item
                    label="Direction"
                    name="direction"
                >
                    <Select
                        placeholder="Select direction"
                        allowClear
                        aria-label="phone-direction-select"
                        options={[
                            { value: 'inbound', label: 'Inbound' },
                            { value: 'outbound', label: 'Outbound' },
                        ]}
                        variant='filled'
                    />
                </Form.Item>

                <Form.Item
                    label="Phone Number"
                    name="phoneNumber"
                >
                    <TelephoneUI
                        disabled={disabled}
                        aria-label="phone-number-input"
                    />
                </Form.Item>

                <Form.Item 
                    label="Notes" 
                    name="notes"
                    rules={[
                        { max: MAX_LENGTHS.longText, message: `Notes cannot exceed ${MAX_LENGTHS.longText} characters` }
                    ]}
                >
                    <Input.TextArea 
                        autoSize={{ minRows: 3, maxRows: 8 }} 
                        placeholder="Add call details" 
                        aria-label="phone-notes-input"
                        maxLength={MAX_LENGTHS.longText}
                        showCount
                        style={{ marginBottom: 10 }}
                        variant='filled'
                    />
                </Form.Item>
            </Form>
            <FlexBox stretch>
                    <Button 
                        onClick={handleCancel} 
                        aria-label="cancel-phone-edit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleSave} 
                        aria-label="save-phone-edit"
                    >
                        Save
                    </Button>
            </FlexBox>
        </FlexCol>
    );
}
export default PhoneCallPopoverForm;
